import React, { Component, Fragment } from 'react';
import classNames from 'classnames';
import debounce from 'lodash.debounce';

import { IPodcastEpisode } from '@wix/podcasts-common/dist/podcast';
import { loadMoreClick, loadMoreCompleted } from '@wix/bi-logger-podcast/v2';

import { biLogger, biTransaction } from '../../../../client/bi';
import {
  IWithSettingsProps,
  withSettings,
} from '../../../../components/context/settings';
import { IWithTPAProps, withTPA } from '../../../../components/context/tpa';
import {
  IWithConfigProps,
  withConfig,
} from '../../../../components/context/config';
import { BiEvent } from '../../../../lib/bi/types';
import getFeedUrl from '../../../../lib/settings/getFeedUrl';
import compose from '../../../../lib/utils/compose';
import {
  getEpisodesPerPage,
  isGridLayoutSelected,
} from '../../../../selectors/settings';

import {
  loadPodcastEpisodes,
  loadPodcastEpisodesCount,
} from '../../MainPage.feedApi';

import { EpisodesSearchResults } from './EpisodesSearch';
import EpisodesGrid from './EpisodesGrid';
import EpisodesList from './EpisodesList';
import EpisodesLoadMore from './EpisodesLoadMore';
import NoEpisodes from './NoEpisodes';
import EpisodesToolbar from './EpisodesToolbar';

import { navigateToMainPage } from '../../../../components/nav/IndexLink';

import styles from './Episodes.scss';
import getStyles from './Episodes.styles';
import {
  withPageAnnouncement,
  IWithPageAnnouncementProps,
} from '../../../../lib/a11y/PageAnnouncementContext';

interface IMainPageEpisodesProps
  extends IWithSettingsProps,
    IWithTPAProps,
    IWithConfigProps,
    IWithPageAnnouncementProps {
  params: Record<string, string>;
  podcastImage: string;
  episodes: IPodcastEpisode[];
  episodesCount: number;
}

interface IMainPageEpisodesState {
  searchMode: boolean;
  isSearchLoading: boolean;
  searchQuery: string;
  searchEpisodes: IPodcastEpisode[];
  searchEpisodesCount: number;
  moreEpisodes: IPodcastEpisode[];
  isMoreEpisodesLoading: boolean;
  focusIndex?: number;
}

function last(array) {
  return array[array.length - 1];
}

function getSearchQuery(params: Record<string, string>): string {
  return params.query ? decodeURIComponent(params.query) : '';
}

class MainPageEpisodes extends Component<
  IMainPageEpisodesProps,
  IMainPageEpisodesState
> {
  private _isMounted: boolean;

  state: IMainPageEpisodesState = {
    searchMode: false,
    isSearchLoading: false,
    searchQuery: getSearchQuery(this.props.params),
    searchEpisodes: [],
    searchEpisodesCount: 0,
    moreEpisodes: [],
    isMoreEpisodesLoading: false,
  };

  loadEpisodesPage({
    query,
    after,
  }: {
    query?: string;
    after?: string;
  }): Promise<IPodcastEpisode[]> {
    const { settings, config, tpa } = this.props;
    return loadPodcastEpisodes({
      url: getFeedUrl(settings, config.defaultFeedUrl),
      query,
      after,
      limit: getEpisodesPerPage(
        settings,
        tpa.params.deviceType,
        window.innerWidth,
      ),
    });
  }

  loadEpisodesCount({ query }: { query?: string }): Promise<number> {
    const { settings, config } = this.props;

    return loadPodcastEpisodesCount({
      url: getFeedUrl(settings, config.defaultFeedUrl),
      query,
    });
  }

  noEpisodes() {
    const { episodesCount } = this.props;
    return episodesCount === 0;
  }

  getEpisodes() {
    const { searchMode, searchEpisodes, moreEpisodes } = this.state;
    const { episodes } = this.props;

    return (searchMode ? searchEpisodes : episodes).concat(moreEpisodes);
  }

  getEpisodesCount() {
    const { searchMode, searchEpisodesCount } = this.state;
    const { episodesCount } = this.props;

    return searchMode ? searchEpisodesCount : episodesCount;
  }

  handleSearchChange = (query) => {
    if (!query) {
      return this.setState({
        searchQuery: '',
        searchEpisodes: [],
        searchEpisodesCount: 0,
        moreEpisodes: [],
        searchMode: false,
      });
    }

    this.setState({ searchQuery: query });

    return this.search(query);
  };

  performSearch = async (query) => {
    try {
      this.setState({
        searchQuery: query,
        isSearchLoading: true,
      });

      const [episodes, episodesCount] = await Promise.all([
        this.loadEpisodesPage({ query }),
        this.loadEpisodesCount({ query }),
      ]);

      if (this._isMounted) {
        this.setState({
          searchEpisodes: episodes,
          searchEpisodesCount: episodesCount,
          moreEpisodes: [],
          isSearchLoading: false,
          searchMode: true,
          focusIndex: undefined,
        });
        this.props.pageAnnouncers.announceSearch(query, episodesCount);
      }
    } catch (e) {
      console.error('search failed', e);

      if (this._isMounted) {
        this.setState({
          isSearchLoading: false,
        });
      }
    }
  };

  search = debounce(this.performSearch, 500);

  handleLoadMore = async () => {
    const { moreEpisodes } = this.state;

    const lastEpisode = last(this.getEpisodes());

    this.setState({
      isMoreEpisodesLoading: true,
      focusIndex: undefined,
    });

    const transaction = biTransaction.start(BiEvent.FEED_LOAD_MORE_REQUESTED);
    const biParams = {
      // TODO: uncomment if need correlationId
      // correlationId: transaction.id,
    };

    try {
      biLogger.report(loadMoreClick(biParams));

      const _moreEpisodes = await this.loadEpisodesPage({
        query: this.state.searchQuery,
        after: lastEpisode.id,
      });

      if (!this._isMounted) {
        return;
      }
      this.setState({
        moreEpisodes: moreEpisodes.concat(_moreEpisodes),
        isMoreEpisodesLoading: false,
        focusIndex:
          this.props.episodes.length +
          moreEpisodes.length -
          (_moreEpisodes.length ? 0 : 1),
      });

      biLogger.report(
        loadMoreCompleted({
          ...biParams,
          episodesCount: _moreEpisodes.length,
          loadTime: biTransaction.end(transaction).duration,
        }),
      );
      this.props.pageAnnouncers.announceLoadMore(_moreEpisodes.length);
    } catch (e) {
      if (this._isMounted) {
        this.setState({
          isMoreEpisodesLoading: false,
          focusIndex: undefined,
        });
      }
    }
  };

  componentDidMount() {
    this._isMounted = true;

    const searchQuery = getSearchQuery(this.props.params);
    if (searchQuery) {
      void this.performSearch(searchQuery);

      // NOTE: clear query param after loaded.
      // NOTE: setTimeout to be sure it will perform after state change processed.
      setTimeout(() => navigateToMainPage(''), 100);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    const { podcastImage, settings, tpa } = this.props;
    const {
      searchMode,
      searchQuery,
      isSearchLoading,
      isMoreEpisodesLoading,
      focusIndex,
    } = this.state;

    const mainSettings = settings.main;

    const dynamicStyle = getStyles({
      colors: settings.colors,
      fonts: settings.fonts,
    });

    const episodes = this.getEpisodes();
    const episodesCount = this.getEpisodesCount();

    const isGridLayout = isGridLayoutSelected(settings, tpa.params.deviceType);

    return (
      <div
        className={classNames(
          styles.episodesWrapper,
          dynamicStyle.episodesWrapper,
        )}
      >
        <div
          data-hook="main.episodes"
          data-layout={isGridLayout ? 'grid' : 'list'}
          className={classNames(styles.episodesContainer, {
            [styles.noHeader]: !mainSettings.showHeader,
            [styles.gridLayout]: isGridLayout,
          })}
        >
          {this.noEpisodes() ? (
            <NoEpisodes dynamicStyle={dynamicStyle} />
          ) : (
            <Fragment>
              <EpisodesToolbar
                settings={settings}
                tpa={tpa}
                onSearchChange={this.handleSearchChange}
                searchQuery={searchQuery}
              />
              <EpisodesSearchResults
                query={searchQuery}
                isLoading={isSearchLoading}
                hasResults={episodes.length > 0}
                onSearch={this.handleSearchChange}
              />
              {isGridLayout ? (
                <EpisodesGrid
                  episodes={episodes}
                  podcastImage={podcastImage}
                  searchMode={searchMode}
                  focusIndex={focusIndex}
                  isLoading={isSearchLoading || isMoreEpisodesLoading}
                />
              ) : (
                <EpisodesList
                  episodes={episodes}
                  podcastImage={podcastImage}
                  searchMode={searchMode}
                  focusIndex={focusIndex}
                  isLoading={isSearchLoading || isMoreEpisodesLoading}
                />
              )}

              {episodes.length < episodesCount && (
                <EpisodesLoadMore
                  isLoading={isMoreEpisodesLoading}
                  onLoadMore={this.handleLoadMore}
                />
              )}
            </Fragment>
          )}
        </div>
      </div>
    );
  }
}

export default compose(
  withTPA,
  withSettings,
  withConfig,
  withPageAnnouncement,
)(MainPageEpisodes);
